// app/components/chat/ProjectRulesModal.tsx
import { useEffect, useState } from 'react';
import { rulesStore, setProjectRules } from '~/lib/stores/rules';
import { useStore } from '@nanostores/react';
import { Modal } from '~/components/ui/Modal';
import { projectId } from '~/lib/persistence/useChatHistory';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/remix';
import { useTranslation } from '~/lib/hooks/useTranslation';

interface ProjectRulesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ProjectRulesModal({ isOpen, onClose }: ProjectRulesModalProps) {
  const { t } = useTranslation();
  const { projectRules, loaded } = useStore(rulesStore);
  const [rules, setRules] = useState(projectRules || '');
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'success' | 'error'>('idle');
  const currentProjectId = useStore(projectId);

  useEffect(() => {
    if (projectRules && loaded) {
      setRules(projectRules);
    }
  }, [projectRules, loaded]);

  useEffect(() => {
    if (!isOpen) {
      setSaveStatus('idle');
    }
  }, [isOpen]);

  const handleSave = async () => {
    try {
      setSaving(true);
      setSaveStatus('saving');

      console.log(
        '[useChatHistory] Saving rules:',
        currentProjectId ? `for project: ${currentProjectId}` : 'as temporary rules in localStorage',
      );

      // Use the optimized setProjectRules which returns success status
      const success = await setProjectRules(rules);

      if (success) {
        setSaveStatus('success');
        console.log('[useChatHistory] Rules saved successfully');

        setTimeout(() => {
          onClose();
          setSaveStatus('idle');
        }, 1000);
      } else {
        setSaveStatus('error');
        toast.error('Failed to save rules. Please try again.');
      }
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          message: 'Error saving project rules',
          rules,
          projectId: currentProjectId,
        },
      });
      console.error('[ProjectRulesModal] Error saving rules:', error);
      setSaveStatus('error');
      toast.error('Failed to save rules. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="w-full max-w-[500px] bg-dualite-background-tertiary border border-dualite-border-secondary rounded-lg shadow-2xl relative overflow-hidden">
        <div className="p-4">
          <h2 className="text-base font-semibold text-dualite-text-modal-primary mb-1">
            {t('modals.projectRules.title')}
          </h2>

          <div className="h-px bg-dualite-border-primary my-3 -mx-6" />

          <div className="mb-4">
            <p className="text-xs leading-relaxed text-dualite-text-modal-secondary mb-2">
              {currentProjectId ? t('modals.projectRules.chatDescription') : t('modals.projectRules.tempDescription')}
            </p>

            {!currentProjectId && (
              <div className="text-xs text-dualite-text-modal-hint bg-dualite-background-tertiary/20 p-3 rounded-lg mb-2 border border-white/10 flex">
                <div className="flex items-center">
                  <div className="i-ph:info text-xs mr-1   w-8 h-8" />
                  <span className="leading-relaxed">
                    {t('modals.projectRules.infoMessage')}{' '}
                    <a
                      href="https://alphadocs.dualite.dev/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-400 hover:text-blue-300 transition-colors underline underline-offset-2"
                    >
                      {t('modals.projectRules.learnMoreLink')}
                    </a>
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-col space-y-3">
            <textarea
              value={rules}
              onChange={(e) => setRules(e.target.value)}
              className="w-full h-64 p-3 bg-dualite-background-tertiary text-dualite-text-modal-primary border border-dualite-border-secondary rounded-lg placeholder-dualite-text-placeholder text-sm focus:outline-none focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              placeholder={t('modals.projectRules.placeholder')}
              disabled={saving}
            />

            <div className="flex justify-end gap-2 mt-3">
              <button
                onClick={onClose}
                className="flex items-center justify-center gap-1.5 px-4 py-2.5 bg-dualite-background-button hover:bg-dualite-background-button-hover rounded-lg text-dualite-text-secondary text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={saving}
              >
                {t('modals.projectRules.cancel')}
              </button>

              <button
                onClick={handleSave}
                disabled={saving}
                className={`flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  saveStatus === 'success'
                    ? 'bg-green-600 hover:bg-green-700 text-dualite-text-white'
                    : saveStatus === 'error'
                      ? 'bg-red-600 hover:bg-red-700 text-dualite-text-white'
                      : 'bg-dualite-background-cta text-dualite-text-cta hover:bg-dualite-background-cta-hover'
                }`}
              >
                {saving ? (
                  <>
                    <div className="i-svg-spinners:90-ring-with-bg h-4 w-4" />
                    <span>{t('modals.projectRules.saving')}</span>
                  </>
                ) : saveStatus === 'success' ? (
                  <>
                    <div className="i-ph:check-bold h-4 w-4" />
                    <span>{t('modals.projectRules.saved')}</span>
                  </>
                ) : saveStatus === 'error' ? (
                  <>
                    <div className="i-ph:x-bold h-4 w-4" />
                    <span>{t('modals.projectRules.error')}</span>
                  </>
                ) : (
                  <span>{t('modals.projectRules.saveRules')}</span>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}
